import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Alert,
  Paper,
  Container,
  Grid,
} from '@mui/material';
import {
  PhotoCamera,
  CheckCircle,
  Upload,
  Crop,
} from '@mui/icons-material';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import '../styles/PhotoUpload.css';
import api from '../utils/api';

const PhotoUpload = () => {
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [photoExists, setPhotoExists] = useState(false);
  const [existingPhoto, setExistingPhoto] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [crop, setCrop] = useState({
    unit: '%',
    width: 100,
    height: 100,
    x: 0,
    y: 0,
  });
  const [imageSrc, setImageSrc] = useState('');
  const [croppedImage, setCroppedImage] = useState('');
  const [showCrop, setShowCrop] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const imgRef = useRef(null);

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    if (!phone.trim()) {
      setMessage({ type: 'error', text: 'Please enter a phone number' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.get(`/api/get-photo/?phone=${phone}`);
      if (response.data.photo) {
        setPhotoExists(true);
        setExistingPhoto(response.data.photo);
        setShowUpload(false);
      } else {
        setPhotoExists(false);
        setShowUpload(true);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setPhotoExists(false);
        setShowUpload(true);
      } else {
        setMessage({ type: 'error', text: error.response?.data?.message || 'Error checking photo status. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        setSelectedFile(file);
        const reader = new FileReader();
        reader.onload = () => {
          setImageSrc(reader.result);
          setShowCrop(true);
        };
        reader.readAsDataURL(file);
      } else {
        setMessage({ type: 'error', text: 'Please select a valid image file' });
      }
    }
  };

  const getCroppedImg = (image, crop) => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = 200;
    canvas.height = 200;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      200,
      200
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        const reader = new FileReader();
        reader.onload = () => {
          resolve(reader.result);
        };
        reader.readAsDataURL(blob);
      }, 'image/jpeg', 0.8);
    });
  };

  const handleCropComplete = async () => {
    if (imgRef.current && crop.width && crop.height) {
      const croppedImageData = await getCroppedImg(imgRef.current, crop);
      setCroppedImage(croppedImageData);
    }
  };

  const handleUpload = async () => {
    if (!croppedImage) {
      setMessage({ type: 'error', text: 'Please crop the image first' });
      return;
    }

    setUploading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.post('/api/photo-upload/', {
        photo: croppedImage,
        phone: phone,
      });

      if (response.data.message === 'success') {
        setMessage({ type: 'success', text: 'Photo uploaded successfully!' });
        setPhotoExists(true);
        setExistingPhoto(croppedImage);
        setShowUpload(false);
        setShowCrop(false);
        setSelectedFile(null);
        setImageSrc('');
        setCroppedImage('');
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Error uploading photo. Please try again.' 
      });
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setPhone('');
    setPhotoExists(false);
    setExistingPhoto('');
    setShowUpload(false);
    setSelectedFile(null);
    setImageSrc('');
    setCroppedImage('');
    setShowCrop(false);
    setMessage({ type: '', text: '' });
  };

  return (
    <Box className="photo-upload-container">
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }} className="photo-upload-paper">
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Button 
            variant="text" 
            onClick={() => window.location.href = '/'}
            sx={{ mb: 2 }}
          >
            ← Back to Home
          </Button>
        </Box>
        <Typography variant="h4" component="h1" gutterBottom align="center" color="primary">
          SCOPE 2025 Photo Upload
        </Typography>
        
        <Typography variant="body1" gutterBottom align="center" sx={{ mb: 4 }}>
          Upload your photo for SCOPE 2025 registration
        </Typography>

        {message.text && (
          <Alert 
            severity={message.type} 
            sx={{ mb: 3 }}
            className={message.type === 'error' ? 'error-alert' : 'success-alert'}
          >
            {message.text}
          </Alert>
        )}

        {!photoExists && !showUpload && (
          <Box component="form" onSubmit={handlePhoneSubmit}>
            <Typography variant="h6" gutterBottom>
              Enter Your Phone Number
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              We'll check if you have already uploaded a photo
            </Typography>
            
            <TextField
              fullWidth
              label="Phone Number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              margin="normal"
              required
              placeholder="Enter your registered phone number"
              className="phone-input"
            />
            
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Check Photo Status'}
            </Button>
          </Box>
        )}

        {photoExists && (
          <Card className="success-card">
            <CardContent>
              <Typography variant="h6" gutterBottom color="success.main">
                <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} />
                Photo Already Uploaded
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Your photo has already been uploaded and cannot be modified.
              </Typography>
              
              <Box sx={{ textAlign: 'center', my: 2 }}>
                <CardMedia
                  component="img"
                  image={existingPhoto}
                  alt="Uploaded Photo"
                  className="photo-preview"
                  sx={{
                    width: 200,
                    height: 200,
                    objectFit: 'cover',
                    mx: 'auto',
                  }}
                />
              </Box>
              
              <Button
                variant="outlined"
                onClick={resetForm}
                fullWidth
              >
                Check Another Number
              </Button>
            </CardContent>
          </Card>
        )}

        {showUpload && !showCrop && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Upload Your Photo
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Please upload a clear photo of yourself. The photo will be cropped to 200x200 pixels.
            </Typography>
            
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="photo-upload"
              type="file"
              onChange={handleFileSelect}
            />
            <label htmlFor="photo-upload">
              <Button
                variant="contained"
                component="span"
                startIcon={<PhotoCamera />}
                fullWidth
                sx={{ mb: 2 }}
              >
                Select Photo
              </Button>
            </label>
            
            <Button
              variant="outlined"
              onClick={resetForm}
              fullWidth
            >
              Back to Phone Entry
            </Button>
          </Box>
        )}

        {showCrop && imageSrc && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Crop Your Photo
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Drag to select the area you want to crop. The final image will be 200x200 pixels.
            </Typography>
            
            <Box sx={{ textAlign: 'center', mb: 2 }} className="crop-container">
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={handleCropComplete}
                aspect={1}
                circularCrop
              >
                <img
                  ref={imgRef}
                  src={imageSrc}
                  alt="Crop preview"
                  style={{ maxWidth: '100%', maxHeight: '400px' }}
                />
              </ReactCrop>
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setShowCrop(false);
                    setSelectedFile(null);
                    setImageSrc('');
                    setCroppedImage('');
                  }}
                  fullWidth
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  onClick={handleUpload}
                  disabled={uploading || !croppedImage}
                  startIcon={uploading ? <CircularProgress size={20} /> : <Upload />}
                  fullWidth
                >
                  {uploading ? 'Uploading...' : 'Upload Photo'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>
    </Container>
    </Box>
  );
};

export default PhotoUpload;
